import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Admin from "@/pages/Admin";
import { Download, ExternalLink, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PART_ONE_COUNT = 90;
const PAYMENT_QR_IMAGE = "/cna-resources/payment.jpeg";

const studentResources = [
  {
    id: "book-highlights",
    title: "Career Navigation Book Highlights",
    eyebrow: "Career Navigation Science",
    imageLayout: "single",
    images: [
      { src: "/cna-resources/book-cover-right.jpg", alt: "Career Navigation book preview" },
    ],
    bullets: [
      "Self help book",
      "Understand the science behind the assessment",
      "10 Q&A sheets to be filled",
      "Create your career transition strategy",
      "Get the dashboard from the assessment",
      "Undertake this assessment every 5 years to refine that strategy",
    ],
    links: [
      {
        label: "Buy on Amazon",
        href: "https://amzn.in/d/d2vE8mi",
        icon: ExternalLink,
      },
      {
        label: "Watch Overview",
        href: "https://youtu.be/N5g8wzcy8eY?si=luxZqFdcMMEBEY1K&t=157",
        icon: ExternalLink,
      },
    ],
  },
  {
    id: "copyright-preview",
    title: "Career Navigation Assessment Instrument",
    eyebrow: "Program Overview",
    imageLayout: "single",
    images: [
      { src: "/cna-resources/book-cover-left.jpg", alt: "Career Navigation copyright preview" },
    ],
    bullets: [
      "Achieve your career goals in line with your definition of career success.",
      "Get ready to change and learn at your present stage in your career.",
      "Shortlist organisations and roles that are best suited for you.",
      {
        prefix: "This assessment instrument has a copyright no ",
        value: "L-139380/2023",
        middle: " dated ",
        date: "26/12/2023",
        suffix: " of Vedanta Edulearn Services, LLP",
      },
    ],
    links: [],
    previewable: true,
    emphasisBulletIndex: 3,
  },
];

const Dashboard = () => {
  const navigate = useNavigate();
  const role = localStorage.getItem("userRole") || "student";
  const [email, setEmail] = useState("");
  const [testHistory, setTestHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [sections, setSections] = useState([]);
  const [nextSection, setNextSection] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [hasQuizProgress, setHasQuizProgress] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [hasSubmittedResult, setHasSubmittedResult] = useState(false);
  const [examEnabled, setExamEnabled] = useState(true);
  const [continueLabel, setContinueLabel] = useState("Continue Assessment");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [previewImage, setPreviewImage] = useState(null);

  const loadDashboard = async (userEmail) => {
    setLoading(true);
    const enabled = await fetchAuthStatus(userEmail);
    await fetchPaymentAmount();
    if (!enabled) {
      setLoading(false);
      return;
    }
    await Promise.all([
      fetchUserData(userEmail),
      fetchSections(userEmail),
      fetchNextSection(userEmail),
      fetchQuizStatus(userEmail)
    ]);
    setLoading(false);
  };

  const fetchUserData = async (userEmail) => {
    try {
      const response = await axios.get(`${API}/user-stats/${userEmail}`);
      setTestHistory(response.data.test_history || []);
      setStats(response.data.stats || null);
      if ((response.data.test_history || []).length > 0 || (response.data.stats?.total_tests || 0) > 0) {
        setHasSubmittedResult(true);
        setQuizCompleted(true);
      }
    } catch (error) {
      setTestHistory([]);
      setStats(null);
    }
  };

  const fetchAuthStatus = async (userEmail) => {
    try {
      const response = await axios.get(`${API}/auth/status`, { params: { email: userEmail } });
      const enabled = response.data?.exam_enabled !== false;
      setExamEnabled(enabled);
      localStorage.setItem("examEnabled", enabled ? "true" : "false");
      return enabled;
    } catch {
      setExamEnabled(true);
      return true;
    }
  };

  const fetchPaymentAmount = async () => {
    try {
      const response = await axios.get(`${API}/config/payment-amount`);
      setPaymentAmount(response.data?.payment_amount || "");
    } catch {
      setPaymentAmount("");
    }
  };

  const fetchSections = async (userEmail) => {
    try {
      const response = await axios.get(`${API}/sections`, {
        params: { email: userEmail }
      });
      setSections(response.data || []);
    } catch (error) {
      setSections([]);
    }
  };

  const fetchNextSection = async (userEmail) => {
    try {
      const response = await axios.get(`${API}/next-section`, {
        params: { email: userEmail }
      });

      setNextSection(response.data?.next_section || null);
      setProgress({
        completed: response.data?.completed_sections || 0,
        total: response.data?.total_sections || 0
      });
    } catch (error) {
      setNextSection(null);
      setProgress({ completed: 0, total: 0 });
      if (error.response?.status === 404) {
        toast.error("No sections are available yet");
      } else if (error.response?.status !== 403) {
        toast.error(error.response?.data?.detail || "Failed to load next section");
      }
    }
  };

  const fetchQuizStatus = async (userEmail) => {
    try {
      const response = await axios.get(`${API}/quiz/status`, {
        params: { email: userEmail }
      });
      const status = response.data || {};
      const inProgress =
        status.status === "in_progress" && (status.current_index || 0) > 0;
      setHasQuizProgress(inProgress);
      if (inProgress) {
        const currentIndex = Number(status.current_index || 0);
        setContinueLabel(currentIndex >= PART_ONE_COUNT ? "Continue Part 2" : "Continue Part 1");
      } else {
        setContinueLabel("Continue Assessment");
      }
      if (status.status === "completed") {
        setQuizCompleted(true);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response?.data?.detail || "Assessment access disabled");
      }
      setHasQuizProgress(false);
      setContinueLabel("Continue Assessment");
    }
  };

  useEffect(() => {
    if (role === "admin") return;
    const userEmail = localStorage.getItem("userEmail");
    const verified = localStorage.getItem("userVerified") === "true";
    if (!userEmail) {
      navigate("/register");
      return;
    }
    if (!verified) {
      navigate("/verify-otp");
      return;
    }
    setEmail(userEmail);
    loadDashboard(userEmail);
    if (localStorage.getItem("quiz_complete")) {
      setJustCompleted(true);
      localStorage.removeItem("quiz_complete");
      setQuizCompleted(true);
    }
  }, [navigate, role]);

  if (role === "admin") {
    return <Admin />;
  }

  const handleStartSection = () => {
    if (!examEnabled) {
      toast.error("Your assessment access is disabled. Contact admin.");
      return;
    }
    if (quizCompleted || hasSubmittedResult) {
      toast.error("You already participated in the assessment.");
      return;
    }
    navigate("/quiz");
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="page-container">
          <div className="auth-card">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="dashboard-page" data-testid="dashboard-page">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div>
              <h1>Welcome back!</h1>
              {!quizCompleted && !hasSubmittedResult && (
                <p>Start your assessment. We will take you to the next assessment automatically.</p>
              )}
            </div>
          </div>

          {justCompleted && (
            <div className="auth-card" style={{ maxWidth: "100%", marginBottom: "1rem" }}>
              <p>You submitted the assessment. Thank you!</p>
            </div>
          )}
          {hasSubmittedResult && !justCompleted && (
            <div className="auth-card" style={{ maxWidth: "100%", marginBottom: "1rem" }}>
              <p>Assessment completed.</p>
              <p style={{ marginTop: "0.35rem", color: "#475569" }}>You already participated in the assessment.</p>
            </div>
          )}

          {!quizCompleted && !hasSubmittedResult && (
            <div className="section-list">
              <div className="section-list-header">
                <div>
                  <h2>Ready to Begin</h2>
                  <p>Click start to begin or continue your assessment.</p>
                </div>
                <button
                  className="btn-primary"
                  onClick={handleStartSection}
                  disabled={!examEnabled}
                >
                  {!examEnabled
                    ? "Assessment Disabled"
                    : hasQuizProgress
                      ? continueLabel
                      : "Start Assessment"}
                </button>
              </div>
              {!examEnabled && (
                <div className="assessment-payment-card">
                  <div className="assessment-payment-copy">
                    <span className="assessment-payment-eyebrow">Assessment Access Required</span>
                    <h3>Activate your assessment</h3>
                    <p className="assessment-payment-text">
                      To activate your assessment, please make a payment of{" "}
                      <strong>{paymentAmount ? `Rs. ${paymentAmount}` : "the applicable amount"}</strong>{" "}
                      using the provided QR code.
                    </p>
                    <p className="assessment-payment-text">
                      Once the payment is completed, please send the payment screenshot to any one of
                      the following email addresses for verification and activation.
                    </p>
                    <div className="assessment-payment-emails">
                      <a href="mailto:Menon.narayan63@gmail.com">Menon.narayan63@gmail.com</a>
                      <a href="mailto:DevendraBora@yahoo.com">DevendraBora@yahoo.com</a>
                    </div>
                  </div>
                  <div className="assessment-payment-qr-panel">
                    <div className="assessment-payment-qr-frame">
                      <img
                        src={PAYMENT_QR_IMAGE}
                        alt="UPI payment QR code for assessment activation"
                        className="assessment-payment-qr-image"
                      />
                    </div>
                    <p className="assessment-payment-qr-caption">
                      Scan this QR code to complete the payment.
                    </p>
                  </div>
                </div>
              )}
              {examEnabled && !nextSection && progress.total === 0 && (
                <div className="auth-card" style={{ maxWidth: "100%", boxShadow: "none", border: "1px solid #e2e8f0" }}>
                  <p>No sections are available yet. Please check back later.</p>
                </div>
              )}
            </div>
          )}

          <section className="dashboard-resource-section" aria-labelledby="student-resources-title">
            <div className="dashboard-resource-header">
              <div>
                <span className="dashboard-resource-eyebrow">Student Resources</span>
                <h2 id="student-resources-title">Career Navigation resources</h2>
                <p>Reference material shared with assessees to understand the assessment and the roadmap around it.</p>
              </div>
            </div>

            <div className="dashboard-resource-grid">
              {studentResources.map((resource) => (
                <article key={resource.id} className="dashboard-resource-card">
                  <div className="dashboard-resource-copy">
                    <span className="dashboard-resource-tag">{resource.eyebrow}</span>
                    <h3>{resource.title}</h3>
                    <ul className="dashboard-resource-list">
                      {resource.bullets.map((item, index) => (
                        <li
                          key={typeof item === "string" ? item : `${item.value}-${item.date}`}
                          className={resource.emphasisBulletIndex === index ? "dashboard-resource-list-emphasis" : ""}
                        >
                          {typeof item === "string" ? (
                            item
                          ) : (
                            <>
                              {item.prefix}
                              <span className="dashboard-resource-inline-highlight">{item.value}</span>
                              {item.middle}
                              <span className="dashboard-resource-inline-highlight">{item.date}</span>
                              {item.suffix}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                    {resource.links.length > 0 && (
                      <div className="dashboard-resource-links">
                        {resource.links.map((link) => {
                          const Icon = link.icon;
                          return (
                            <a
                              key={link.href}
                              className="dashboard-resource-link"
                              href={link.href}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Icon size={16} />
                              <span>{link.label}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className={`dashboard-resource-media dashboard-resource-media-${resource.imageLayout}`}>
                    {resource.images.map((image) => (
                      resource.previewable ? (
                        <button
                          key={image.src}
                          type="button"
                          className="dashboard-resource-image-button"
                          onClick={() => setPreviewImage(image)}
                        >
                          <div className="dashboard-resource-image-frame">
                            <img src={image.src} alt={image.alt} className="dashboard-resource-image" />
                          </div>
                        </button>
                      ) : (
                        <div key={image.src} className="dashboard-resource-image-frame">
                          <img src={image.src} alt={image.alt} className="dashboard-resource-image" />
                        </div>
                      )
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </section>

          {previewImage && (
            <div className="dashboard-resource-modal-backdrop" onClick={() => setPreviewImage(null)}>
              <div
                className="dashboard-resource-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Copyright preview"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="dashboard-resource-modal-header">
                  <h3>Copyright Preview</h3>
                  <button
                    type="button"
                    className="dashboard-resource-modal-close"
                    onClick={() => setPreviewImage(null)}
                    aria-label="Close preview"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="dashboard-resource-modal-body">
                  <img src={previewImage.src} alt={previewImage.alt} className="dashboard-resource-modal-image" />
                </div>
                <div className="dashboard-resource-modal-actions">
                  <a
                    href={previewImage.src}
                    download="Career Navigation Assessment.jpg"
                    className="dashboard-resource-link"
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

